
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, HeartPulse, Trophy, Gamepad } from 'lucide-react';
import ArtistJourney from './ArtistJourney';
import ArtistHealthDashboard from '@/components/healthcheck/ArtistHealthDashboard';
import BadgesGrid from './BadgesGrid';
import CareerSimGame from './career-sim/CareerSimGame';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/types/gamification';
import { ArtistHealthCheck } from '@/types/artistHealth';

// Mock data for the artist journey
const mockBadges: Badge[] = [
  {
    id: 'badge-1',
    name: 'First Release',
    description: 'Published your first release',
    icon: '🎵',
    dateEarned: new Date(2023, 5, 10),
    category: 'achievement',
    rarity: 'common'
  },
  {
    id: 'badge-2',
    name: '1K Streams',
    description: 'Reached 1,000 total streams',
    icon: '🔊',
    dateEarned: new Date(2023, 6, 15),
    category: 'milestone',
    rarity: 'common'
  },
  {
    id: 'badge-3',
    name: 'Collaboration Master',
    description: 'Completed 5 collaborative projects',
    icon: '🤝',
    dateEarned: new Date(2023, 7, 20),
    category: 'collaboration',
    rarity: 'uncommon'
  },
  {
    id: 'badge-4',
    name: 'Profile Perfectionist',
    description: 'Completed your artist profile',
    icon: '👤',
    dateEarned: new Date(2023, 4, 5),
    category: 'achievement',
    rarity: 'common'
  }
];

// Mock data for artist health
const mockHealthCheck: ArtistHealthCheck = {
  overallStatus: 'Gaining Momentum',
  scores: {
    overall: 72,
    streaming: 68,
    socialEngagement: 75,
    fanGrowth: 70,
    collaborations: 65,
    marketing: 80,
    content: 78
  },
  growth: {
    streamingGrowth: 15,
    engagementGrowth: 22,
    followerGrowth: 18,
    period: 'month'
  },
  lastUpdated: new Date(2023, 8, 25),
  nextMilestone: {
    description: 'Reach 10,000 monthly listeners',
    current: 7250,
    target: 10000,
    eta: '2 months'
  },
  recommendations: [
    {
      priority: 'High',
      description: 'Increase Release Frequency',
      actionSteps: ['Try to release content every 6-8 weeks to maintain momentum.']
    },
    {
      priority: 'Medium',
      description: 'Expand Social Presence',
      actionSteps: ['Post more consistently on Instagram and TikTok to grow your audience.']
    },
    {
      priority: 'High',
      description: 'Collaborate With Similar Artists',
      actionSteps: ['Find 2-3 artists in your genre with similar audience sizes for collaborations.']
    }
  ]
};

const ProgressTrackingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('journey');
  
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 mb-6">
          <TabsTrigger value="journey" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            <span>Artist Journey</span>
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <HeartPulse className="h-4 w-4" />
            <span>Artist Health</span>
          </TabsTrigger>
          <TabsTrigger value="badges" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span>Badges & Rewards</span>
          </TabsTrigger>
          <TabsTrigger value="simulator" className="flex items-center gap-2">
            <Gamepad className="h-4 w-4" />
            <span>Career Simulator</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="journey">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Your Artist Journey</CardTitle>
            </CardHeader>
            <CardContent>
              <ArtistJourney badges={mockBadges} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="health">
          <ArtistHealthDashboard 
            healthCheck={mockHealthCheck}
            onRequestUpdate={() => {
              console.log('Requesting update for artist health check');
            }}
          />
        </TabsContent>
        
        <TabsContent value="badges">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Badges & Rewards</CardTitle>
            </CardHeader>
            <CardContent>
              <BadgesGrid badges={mockBadges} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="simulator" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Gamepad className="h-5 w-5 text-purple-500" />
                Music Career Simulator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-2 mb-4 border border-purple-500/20 bg-purple-500/5 rounded-md">
                <p className="text-sm text-purple-300">
                  Build your music career from bedroom producer to superstar! Balance your needs, make strategic decisions, and navigate the music industry.
                </p>
              </div>
              <CareerSimGame />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProgressTrackingPage;
